import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import axios from "axios";
import {
  FaChevronRight,
  FaFilePdf,
  FaFileWord,
  FaFileImage,
  FaFileAlt,
} from "react-icons/fa";
import { MdEditSquare, MdDeleteForever, MdSearch, MdClose, MdInbox, MdErrorOutline, MdCheckCircle } from "react-icons/md";
import { useTheme } from "./ThemeContext";
import "./templatePreview.css";

const fileIcon = (file) => {
  const ext = String(file).split(".").pop().toLowerCase();
  if (ext === "pdf") return <FaFilePdf />;
  if (ext === "doc" || ext === "docx") return <FaFileWord />;
  if (["jpg", "jpeg", "png", "gif"].includes(ext)) return <FaFileImage />;
  return <FaFileAlt />;
};

// `attachments` is stored as a JSON string of file names.
const parseFiles = (raw) => {
  if (!raw || raw.length === 0) return { files: [], error: false };
  try {
    const files = JSON.parse(raw);
    return { files: Array.isArray(files) ? files : [], error: false };
  } catch (err) {
    return { files: [], error: true };
  }
};

/*
 * Shared list + detail preview used by the Email and WhatsApp template modals:
 * a searchable list of templates on the left, the selected template on the right
 * with edit / delete actions. The two template types only differ in the endpoints,
 * which field is the "headline", and how the body is rendered, so those are props.
 */
function TemplatePreview({
  fetchPath,
  deletePath,
  headlineLabel,
  getHeadline,
  renderBody,
  bodyLabel = "Message",
  noun = "template",
  Editor,
}) {
  const { isDark } = useTheme();

  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [query, setQuery] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState(null); // { type: "success" | "error", text }

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/${fetchPath}`);
      if (response.data.success) {
        setTemplates(response.data.data);
      } else {
        console.error("Failed to fetch templates:", response.data.message);
      }
    } catch (err) {
      console.error(`Error fetching ${noun}s:`, err);
    } finally {
      setLoading(false);
    }
  }, [fetchPath, noun]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    setConfirming(false);
  }, [selected]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      templates
        .map((t, index) => ({ t, index }))
        .filter(({ t, index }) => !q || (t.template_name || `Template ${index + 1}`).toLowerCase().includes(q)),
    [templates, q],
  );

  if (loading) {
    return (
      <div className={`tpv ${isDark ? "tpv-dark" : ""}`}>
        <div className="tpv-state">
          <Spinner animation="border" size="sm" />
          <span>Loading templates...</span>
        </div>
      </div>
    );
  }

  if (!templates.length) {
    return (
      <div className={`tpv ${isDark ? "tpv-dark" : ""}`}>
        <div className="tpv-empty">
          <span className="tpv-empty-icon"><MdInbox /></span>
          <div className="tpv-empty-title">No templates found</div>
        </div>
      </div>
    );
  }

  const current = templates[selected] || templates[0];
  const currentId = current.template_id ?? current.id;
  const { files, error: badFiles } = parseFiles(current.attachments);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API}/${deletePath}`, { id: currentId });

      if (response.data?.success) {
        setNotice({ type: "success", text: "Template deleted." });
        setConfirming(false);
        const next = templates.filter((t) => (t.template_id ?? t.id) !== currentId);
        setTemplates(next);
        setSelected((prevIdx) => (next.length ? Math.min(prevIdx, next.length - 1) : 0));
      } else {
        setNotice({ type: "error", text: response.data?.message || "Failed to delete template." });
      }
    } catch (error) {
      console.error("Delete error:", error.message);
      setNotice({ type: "error", text: "Server error. Could not delete." });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={`tpv ${isDark ? "tpv-dark" : ""}`}>
      <aside className="tpv-list-pane">
        <div className="tpv-list-head">
          <span className="tpv-list-title">Templates</span>
          <span className="tpv-count">{templates.length}</span>
        </div>

        <div className="tpv-search">
          <MdSearch className="tpv-search-icon" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search templates..."
            aria-label="Search templates"
            className="tpv-search-input"
          />
          {query && (
            <button type="button" className="tpv-search-clear" onClick={() => setQuery("")} aria-label="Clear search">
              <MdClose />
            </button>
          )}
        </div>

        <ul className="tpv-list">
          {visible.length === 0 ? (
            <li className="tpv-list-empty">No templates match “{query}”.</li>
          ) : (
            visible.map(({ t, index }) => (
              <li key={t.template_id ?? t.id ?? index}>
                <button
                  type="button"
                  className={`tpv-item ${selected === index ? "active" : ""}`}
                  aria-current={selected === index ? "true" : undefined}
                  onClick={() => setSelected(index)}
                >
                  <span className="tpv-item-name">{t.template_name || `Template ${index + 1}`}</span>
                  {selected === index && <FaChevronRight aria-hidden="true" />}
                </button>
              </li>
            ))
          )}
        </ul>
      </aside>

      <section className="tpv-detail" aria-live="polite">
        <header className="tpv-detail-head">
          <div className="tpv-headline">
            <span className="tpv-headline-label">{headlineLabel}</span>
            <span className="tpv-headline-text">{getHeadline(current) || "-"}</span>
          </div>

          {confirming ? (
            <div className="tpv-confirm" role="alertdialog" aria-label="Confirm delete">
              <span>Delete this {noun} template?</span>
              <Button className="tpv-btn tpv-btn-ghost" size="sm" onClick={() => setConfirming(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button className="tpv-btn tpv-btn-danger" size="sm" onClick={handleDelete} disabled={deleting}>
                {deleting ? <Spinner size="sm" animation="border" /> : "Delete"}
              </Button>
            </div>
          ) : (
            <div className="tpv-actions">
              <Button className="tpv-btn tpv-btn-primary" size="sm" onClick={() => setShowEdit(true)}>
                <MdEditSquare className="me-1" /> Edit
              </Button>
              <Button className="tpv-btn tpv-btn-outline-danger" size="sm" onClick={() => setConfirming(true)}>
                <MdDeleteForever className="me-1" /> Delete
              </Button>
            </div>
          )}
        </header>

        {notice && (
          <div className={`tpv-notice ${notice.type}`} role={notice.type === "error" ? "alert" : "status"}>
            {notice.type === "error" ? <MdErrorOutline aria-hidden="true" /> : <MdCheckCircle aria-hidden="true" />}
            <span>{notice.text}</span>
          </div>
        )}

        <div className="tpv-detail-body">
          {(files.length > 0 || badFiles) && (
            <div className="tpv-block">
              <div className="tpv-block-label">Attachments</div>
              {badFiles ? (
                <div className="tpv-bad">Invalid attachment format</div>
              ) : (
                <ul className="tpv-files">
                  {files.map((file, i) => (
                    <li key={i} className="tpv-file" title={file}>
                      <span className="tpv-file-icon">{fileIcon(file)}</span>
                      <span className="tpv-file-name">{file}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="tpv-block">
            <div className="tpv-block-label">{bodyLabel}</div>
            {renderBody(current)}
          </div>
        </div>
      </section>

      <Editor
        show={showEdit}
        onClose={() => setShowEdit(false)}
        mode="edit"
        initialTemplate={current}
        onSaved={async () => {
          await fetchTemplates();
          setShowEdit(false);
        }}
      />
    </div>
  );
}

export default TemplatePreview;
